/**
 * A tab left open across a deploy runs an old app against a new server.
 * This notices the new build and loads it at a safe moment: when the
 * person moves to another page, or when the old app has just failed.
 *
 * The build is told apart by the app's entry file (``runtime.js``): it
 * names every lazy chunk, so it changes whenever anything in the app does,
 * whichever repository the change came from. Nothing runs where there is
 * no such file (the bundled desktop and mobile apps) or in development,
 * where it changes on every save.
 */
const EVERY_MS = 5 * 60 * 1000;
const GUARD = "build-check-reloaded";

let loaded: string | null = null;
let stale = false;
let checkedAt = 0;

const entryUrl = (): string | null => {
  if (process.env.NODE_ENV !== "production") return null;
  const tag = document.querySelector<HTMLScriptElement>('script[src*="/react-app/runtime.js"]');

  return tag ? tag.src.split("?")[0] : null;
};

/** A short fingerprint of the entry file as the server has it now. */
export const fingerprint = async (url: string): Promise<string | null> => {
  try {
    const res = await fetch(`${url}?check=${Date.now()}`, { cache: "no-store", credentials: "omit" });

    if (!res.ok) return null;
    const text = await res.text();
    let hash = 5381;

    for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    return `${text.length}:${hash}`;
  } catch (_) {
    return null; // offline: nothing is known, nothing is done
  }
};

/** Ask the server which build it serves; true when it is not this tab's. */
export const isStaleBuild = async (force = false): Promise<boolean> => {
  const url = entryUrl();

  if (!url || !loaded || stale) return stale;
  if (!force && Date.now() - checkedAt < EVERY_MS) return false;
  checkedAt = Date.now();
  const now = await fingerprint(url);

  stale = Boolean(now) && now !== loaded;
  return stale;
};

/** Load the new build, once for each build (a broken server must not loop). */
export const loadNewBuild = (): boolean => {
  try {
    if (sessionStorage.getItem(GUARD) === loaded) return false;
    sessionStorage.setItem(GUARD, loaded ?? "");
  } catch (_) {
    return false;
  }
  window.location.reload();
  return true;
};

/** The old app failed: if a new build is out, load it instead of showing the error. */
export const healIfStale = async (): Promise<boolean> => ((await isStaleBuild(true)) ? loadNewBuild() : false);

export const installBuildCheck = () => {
  const url = entryUrl();

  if (!url) return;
  fingerprint(url).then((print) => {
    loaded = print;
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") isStaleBuild();
  });
  // Moving to another page is the safe moment: nothing on the page left is
  // kept anyway. A change of the query only (the next task, a filter) is
  // not a move.
  const push = history.pushState.bind(history);

  history.pushState = (data: unknown, unused: string, target?: string | URL | null) => {
    const from = location.pathname;

    push(data, unused, target);
    if (location.pathname === from) return;
    if (stale) loadNewBuild();
    else isStaleBuild();
  };
};
